import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { NO_PERMISSION } from "#app/components/menus/disabled-reason";
import type { MenuParts } from "#app/components/menus/menu-parts";
import { viewVisual } from "#app/components/view-options/view-type-meta";
import { useAppData } from "#app/lib/data";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useCopyWidgetBoard } from "#app/lib/hooks/use-copy-widget-board";
import { useCreateEntityView } from "#app/lib/hooks/use-entity-views";
import { usePermission } from "#app/lib/hooks/use-permission";
import { canEdit, canManageSharing } from "#app/lib/utils/access-level";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { AnalyticsContext, DashboardEvent, trackEvent } from "#app/lib/analytics";
import { viewPath } from "#app/lib/views/view-routes";
import { renderDashboardMenuItems } from "./dashboard-menu-items";
import { DashboardShareDialog } from "./dashboard-share-dialog";
import { DeleteDashboardDialog } from "./delete-dashboard-dialog";
import { RenameDashboardDialog } from "./rename-dashboard-dialog";
import { useDashboardFavorites } from "./use-dashboard-favorites";
import { dashboardSnapshot, surfaceRefKey } from "./use-dashboard-surfaces";
import { useRecentDashboards } from "./use-recent-dashboards";
import type { DashboardSurface } from "./use-dashboard-surfaces";
import { workspaceHref } from "#app/lib/utils/entity-hrefs";

/**
 * What the viewer may do to one dashboard, from the row alone.
 *
 * A workspace dashboard carries its own ACL: the creator (the server resolves
 * them to full_edit — the compare keeps a stale cached level from locking an
 * owner out) and `edit` may rename/duplicate; only `full_edit` may delete or
 * manage sharing (`canManageSharing`, the one tier the caps matrix grants
 * `delete`). A workflow dashboard is workspace-shared and every write is
 * gated on `create.views`; its scope's default view stays until the role
 * moves off it (the server refuses that delete too).
 */
function surfaceAbilities(
  surface: DashboardSurface,
  userId: string | null | undefined,
  hasCreateViews: boolean,
): {
  manageDisabledReason: string | null;
  deleteDisabledReason: string | null;
  canShare: boolean;
} {
  const { view, scope } = surface;
  if (scope.kind === "workspace") {
    const isCreator = view.createdBy != null && view.createdBy === userId;
    const canShare = isCreator || canManageSharing(view.viewerAccessLevel);
    return {
      manageDisabledReason:
        isCreator || canEdit(view.viewerAccessLevel) ? null : NO_PERMISSION,
      deleteDisabledReason: canShare ? null : NO_PERMISSION,
      canShare,
    };
  }
  return {
    manageDisabledReason: hasCreateViews ? null : NO_PERMISSION,
    deleteDisabledReason: !hasCreateViews
      ? NO_PERMISSION
      : view.isDefault
        ? "Can't delete the default view"
        : null,
    canShare: false,
  };
}

type DashboardActionsValue = {
  /** The dashboard roster for one surface, through the hosting menu kind's
   *  parts (`CONTEXT_MENU_PARTS` for a right-click). */
  renderMenuItems: (surface: DashboardSurface, parts?: MenuParts) => ReactNode;
};

const DashboardActionsContext = createContext<DashboardActionsValue | null>(
  null,
);

/**
 * Hosts the dashboard row actions' dialogs ONCE for every row under it —
 * rename, delete and share prompts, plus the duplicate write — so a list of
 * rows (the directory table, the module menu's Favorites/Recent) mounts one
 * set of dialogs, not one per row. Rows read `useDashboardActions()` for
 * their menu roster; the roster's handlers arm the dialogs here.
 */
export function DashboardActionsProvider({ children }: { children: ReactNode }) {
  const { currentUser } = useAppData();
  const { activeWorkspace } = useWorkspace();
  const hasCreateViews = usePermission("create.views");
  const { isFavorite, toggleFavorite } = useDashboardFavorites();
  const { recordVisit } = useRecentDashboards();
  const createEntityView = useCreateEntityView();
  const copyWidgetBoard = useCopyWidgetBoard();
  const { push } = useAppNavigate();

  const [renameTarget, setRenameTarget] = useState<DashboardSurface | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DashboardSurface | null>(null);
  const [shareTarget, setShareTarget] = useState<DashboardSurface | null>(null);

  const workspacePath = activeWorkspace?.urlPath ?? "";
  const userId = currentUser?.userId;

  // The view-tab Duplicate grammar: the row copies with its identity and
  // display config, then its widget board (rows keyed by the board key, not
  // part of `config`) is copied in before landing on it. A workspace
  // dashboard's copy reads from the same workflow set and starts PRIVATE to
  // the viewer, like every dashboard they create.
  const duplicate = useCallback(
    async (surface: DashboardSurface) => {
      const { view, scope } = surface;
      const visual = viewVisual(view);
      const viewsScope = { kind: scope.kind, id: scope.id };
      const created = await createEntityView(viewsScope, {
        type: view.type,
        name: `${view.name} copy`,
        icon: { icon: visual.icon, color: visual.color },
        config: view.config,
        ...(scope.kind === "workspace"
          ? { visibility: "private" as const, workflowIds: view.workflowIds }
          : {}),
      });
      if (!created) return;
      trackEvent({
        context: AnalyticsContext.Dashboard,
        event: DashboardEvent.Duplicate,
        data: { via: "row_menu", scope: scope.kind },
      });
      if (view.widgetView && created.widgetView) {
        try {
          await copyWidgetBoard(view.widgetView, created.widgetView);
        } catch (err) {
          // The view row exists — land on it with whatever copied.
          toastMutationError(err, "Failed to copy widgets");
        }
      }
      // Opening the copy is a visit: it heads Recent the moment it exists.
      recordVisit({
        ...dashboardSnapshot(surface),
        viewKey: created.builtInKey ?? created.id,
        name: created.name,
        ...viewVisual(created),
      });
      push(viewPath(viewsScope, created));
    },
    [copyWidgetBoard, createEntityView, push, recordVisit],
  );

  const renderMenuItems = useCallback(
    (surface: DashboardSurface, parts?: MenuParts) => {
      const abilities = surfaceAbilities(surface, userId, hasCreateViews);
      return renderDashboardMenuItems({
        parts,
        href: workspaceHref(workspacePath, surface.route),
        isFavorited: isFavorite(surfaceRefKey(surface)),
        onFavorite: () => toggleFavorite(surface, "row_menu"),
        manageDisabledReason: abilities.manageDisabledReason,
        onRename: () => setRenameTarget(surface),
        onDuplicate: () => void duplicate(surface),
        deleteDisabledReason: abilities.deleteDisabledReason,
        onDelete: () => setDeleteTarget(surface),
        onShare:
          surface.scope.kind === "workspace"
            ? () => setShareTarget(surface)
            : undefined,
        canShare: abilities.canShare,
      });
    },
    [
      duplicate,
      hasCreateViews,
      isFavorite,
      toggleFavorite,
      userId,
      workspacePath,
    ],
  );

  const value = useMemo(() => ({ renderMenuItems }), [renderMenuItems]);

  return (
    <DashboardActionsContext.Provider value={value}>
      {children}
      <RenameDashboardDialog
        view={renameTarget?.view ?? null}
        onOpenChange={(open) => {
          if (!open) setRenameTarget(null);
        }}
      />
      <DeleteDashboardDialog
        surface={deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      />
      <DashboardShareDialog
        view={shareTarget?.view ?? null}
        onOpenChange={(open) => {
          if (!open) setShareTarget(null);
        }}
      />
    </DashboardActionsContext.Provider>
  );
}

export function useDashboardActions(): DashboardActionsValue {
  const value = useContext(DashboardActionsContext);
  if (!value) {
    throw new Error(
      "useDashboardActions must be used within a DashboardActionsProvider",
    );
  }
  return value;
}
